import React, { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../store/AuthContext';
import PointsDisplay from '../profile/PointsDisplay';
import NotificationBell from '../notifications/NotificationBell';
import ThemeToggle from './ThemeToggle';
// import PWAInstallPrompt from '../PWAInstallPrompt';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const prevPathnameRef = useRef(location.pathname);

  // Sync search input with current search query from URL when on search results page
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathnameRef.current;

    if (currentPath === '/search' && prevPath !== '/search') {
      // ONLY sync when navigating TO search page from elsewhere
      const urlQuery = searchParams.get('q');
      if (urlQuery) {
        setSearchQuery(urlQuery);
      }
    } else if (prevPath === '/search' && currentPath !== '/search') {
      // Navigating AWAY from search page - clear input for fresh start
      setSearchQuery('');
    }

    // Update the previous pathname reference
    prevPathnameRef.current = currentPath;
  }, [location.pathname, searchParams]);

  // Get search history from localStorage
  const getSearchHistory = useCallback(() => {
    try {
      const history = localStorage.getItem('searchHistory');
      return history ? JSON.parse(history) : [];
    } catch {
      return [];
    }
  }, []);

  // Save search to history
  const saveToSearchHistory = useCallback(
    (query: string) => {
      try {
        const history = getSearchHistory();
        const trimmedQuery = query.trim();
        if (trimmedQuery && !history.includes(trimmedQuery)) {
          const newHistory = [trimmedQuery, ...history.slice(0, 9)]; // Keep last 10 searches
          localStorage.setItem('searchHistory', JSON.stringify(newHistory));
        }
      } catch (error) {
        console.warn('Could not save search history:', error);
      }
    },
    [getSearchHistory]
  );

  const handleLogout = useCallback(() => {
    logout();
    setMobileMenuOpen(false);
  }, [logout]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent, suggestionQuery?: string) => {
      e.preventDefault();
      const queryToSearch = suggestionQuery || searchQuery.trim();
      if (queryToSearch) {
        // Save to search history
        saveToSearchHistory(queryToSearch);
        // Update search query state to match what we're searching for
        setSearchQuery(queryToSearch);
        // Navigate to search results page with query
        navigate(`/search?q=${encodeURIComponent(queryToSearch)}`);
        // Hide suggestions but keep the search query in the input
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        closeMobileMenu(); // Close mobile menu if open
      }
    },
    [searchQuery, navigate, closeMobileMenu, saveToSearchHistory]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      setSelectedSuggestionIndex(-1);

      // Debounce suggestions to improve performance
      if (value.trim().length > 0) {
        setTimeout(() => {
          if (value === searchInputRef.current?.value) {
            // Check if value hasn't changed
            // Get search history for suggestions
            const history = getSearchHistory();
            const filteredHistory = history.filter((item: string) =>
              item.toLowerCase().includes(value.toLowerCase())
            );
            setSearchSuggestions(filteredHistory);
            setShowSuggestions(true);
          }
        }, 300); // 300ms debounce
      } else {
        setShowSuggestions(false);
        setSearchSuggestions([]);
      }
    },
    [getSearchHistory]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (selectedSuggestionIndex >= 0 && searchSuggestions[selectedSuggestionIndex]) {
          handleSearchSubmit(e as any, searchSuggestions[selectedSuggestionIndex]);
        } else {
          handleSearchSubmit(e as any);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (showSuggestions && searchSuggestions.length > 0) {
          setSelectedSuggestionIndex((prev) =>
            prev < searchSuggestions.length - 1 ? prev + 1 : 0
          );
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (showSuggestions && searchSuggestions.length > 0) {
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : searchSuggestions.length - 1
          );
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        searchInputRef.current?.blur();
      }
    },
    [handleSearchSubmit, showSuggestions, searchSuggestions, selectedSuggestionIndex]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSearchSubmit(fakeEvent, suggestion);
    },
    [handleSearchSubmit]
  );

  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length > 0) {
      setShowSuggestions(true);
    }
  }, [searchQuery]);

  const handleSearchBlur = useCallback(() => {
    // Delay hiding suggestions to allow click events to register
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 150);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    searchInputRef.current?.focus();
  }, []);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  return (
    <nav className="navbar">
      <div className="container navbar-container">
        <div className="navbar-left">
          <div className="logo">
            <Link to="/" onClick={closeMobileMenu}>
              <img
                src="/images/logos/deals-n-steals-logo-solo-blue-optimized.svg"
                alt="Deals n Steals logo"
                className="logo-image"
                width="184"
                height="179"
              />
              <span className="logo-text">Deals n Steals</span>
            </Link>
          </div>
        </div>

        <div className="navbar-center">
          <div className="search-wrapper">
            <form className="search-container" onSubmit={handleSearchSubmit}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search deals..."
                className="search-input"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                aria-label="Search for deals"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="search-clear-btn"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
              <button type="submit" className="search-btn" aria-label="Submit search">
                Search
              </button>
            </form>

            {showSuggestions && searchSuggestions.length > 0 && (
              <div ref={suggestionsRef} className="search-suggestions">
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`search-suggestion ${
                      index === selectedSuggestionIndex ? 'selected' : ''
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  >
                    <span className="suggestion-icon">🔍</span>
                    <span className="suggestion-text">{suggestion}</span>
                    <span className="suggestion-label">Recent</span>
                  </div>
                ))}
                {searchSuggestions.length === 0 && searchQuery.trim() && (
                  <div className="search-suggestion no-suggestions">
                    <span className="suggestion-icon">💡</span>
                    <span className="suggestion-text">No recent searches found</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="navbar-right">
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            {mobileMenuOpen ? '✕' : '☰'}
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'nav-open' : ''}`}>
            <li>
              <Link to="/deals" onClick={closeMobileMenu}>
                All Deals
              </Link>
            </li>
            <li>
              <Link to="/trending" onClick={closeMobileMenu}>
                Trending
              </Link>
            </li>
            <li>
              <Link to="/articles" onClick={closeMobileMenu}>
                Guides
              </Link>
            </li>
            <li>
              <Link to="/categories" onClick={closeMobileMenu}>
                Categories
              </Link>
            </li>
            {isAuthenticated ? (
              <>
                <li>
                  <Link to="/submit" rel="nofollow" onClick={closeMobileMenu}>
                    Submit Deal
                  </Link>
                </li>
                <li className="notification-nav-item">
                  <NotificationBell />
                </li>
                <li className="theme-toggle-nav-item">
                  <ThemeToggle />
                </li>
                <li className="user-nav-item">
                  <Link
                    to="/profile"
                    className="profile-link"
                    rel="nofollow"
                    onClick={closeMobileMenu}
                  >
                    <span className="username">{user?.username}</span>
                    {isAuthenticated && <PointsDisplay minimal={true} />}
                  </Link>
                </li>
                <li>
                  <button onClick={handleLogout} className="logout-btn">
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li className="theme-toggle-nav-item">
                  <ThemeToggle />
                </li>
                <li>
                  <Link to="/login" onClick={closeMobileMenu}>
                    Login
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="btn-register" onClick={closeMobileMenu}>
                    Register
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default React.memo(Navbar);
